import { useState } from "react";
import { api } from "../lib/api";
import { useResource } from "../lib/hooks";
import type { Participant } from "../lib/types";

interface ComplianceResult {
    pack: { id: string; name: string };
    items: Array<{ id: string; kind: "document" | "form"; reference: string; label: string; met: boolean }>;
    score: number;
}

/**
 * Which pack this participant is held to, and how the record scores against
 * it right now — computed live server-side from what is actually on file.
 */
export const ParticipantComplianceCard = ({ participant, onSaved }: {
    participant: Participant & { compliance_pack_id?: string | null };
    onSaved: () => void;
}) => {
    const packs = useResource<Array<{ id: string; name: string; is_default: boolean }>>("/compliance-packs");
    const compliance = useResource<ComplianceResult>(`/participants/${participant.id}/compliance`);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const setPack = async (packId: string) => {
        setBusy(true);
        setError(null);
        try {
            await api.put(`/participants/${participant.id}`, { compliance_pack_id: packId || null });
            onSaved();
            compliance.reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not change the pack");
        } finally {
            setBusy(false);
        }
    };

    const result = compliance.data;

    return (
        <div className="card">
            <div className="card-head">
                <h2>Participant compliance</h2>
                {result && (
                    <span className={`chip ${result.score === 100 ? "pass" : result.score >= 50 ? "warn" : "block"}`}>
                        {result.score}%
                    </span>
                )}
            </div>
            <div className="card-body" style={{ display: "grid", gap: "0.55rem" }}>
                {error && <div className="error-banner">{error}</div>}
                <div className="field" style={{ marginBottom: 0 }}>
                    <label htmlFor="pc-pack">Compliance pack</label>
                    <select id="pc-pack" disabled={busy}
                        value={participant.compliance_pack_id ?? ""}
                        onChange={(e) => void setPack(e.target.value)}>
                        <option value="">Default{(packs.data ?? []).find((p) => p.is_default) ? ` (${(packs.data ?? []).find((p) => p.is_default)!.name})` : ""}</option>
                        {(packs.data ?? []).filter((p) => !p.is_default).map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                    <span className="help">The documents and forms this record needs to count as complete.</span>
                </div>
                {(result?.items ?? []).map((item) => (
                    <div key={item.id} className="file-row">
                        <span className={`chip ${item.met ? "pass" : "warn"}`}>{item.met ? "On file" : "Missing"}</span>
                        <span style={{ fontWeight: 600 }}>{item.label}</span>
                        <span style={{ fontSize: "0.72rem", color: "var(--muted)" }} className="mono">{item.reference}</span>
                    </div>
                ))}
                {compliance.loading && <p style={{ color: "var(--muted)", fontSize: "0.82rem" }}>Checking the record…</p>}
                {result && result.items.length === 0 && (
                    <p style={{ color: "var(--muted)", fontSize: "0.82rem" }}>This pack has no items yet.</p>
                )}
            </div>
        </div>
    );
};
